// The skill library (views/Skills/SkillLibraryPage.vue): cards grouped per
// category, filtered by search, category, "mine", "waiting for you" and
// "feedback for you". Pure,
// so it is
// unit-tested in tests/unit/skillLibrary.test.js.

const UNCATEGORIZED = "Uncategorized";

// One Valsplat ramp per category, the same pairing the kennisbank uses
// (kennisbank/src/lib/categories.js), so a category looks the same in both.
const KNOWN_RAMPS = {
  "ux research": "blauw",
  "neuro ux research": "paars",
  sales: "geel",
  "service design": "turquoise",
  "ux design": "rood",
};
const FALLBACK_RAMPS = ["blauw", "turquoise", "geel", "groen", "oranje"];

export function cleanCategory(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

// The token ramp ("blauw", "paars", …) for a category; unknown categories get
// a stable pick from the fallback list.
export function categoryRamp(category) {
  const key = cleanCategory(category).toLocaleLowerCase();
  if (KNOWN_RAMPS[key]) return KNOWN_RAMPS[key];
  let h = 0;
  for (const c of key) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return FALLBACK_RAMPS[h % FALLBACK_RAMPS.length];
}

export function firstSentence(text) {
  const clean = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  return clean.split(/(?<=[.!?])\s+(?=["'(]?[A-Z])/)[0];
}

// The curated kennisbank summary (kb_skill_meta/{id}.summary), else the first
// sentence of the live description, else of the working description.
export function skillSummary(skill, meta) {
  const curated = meta && String(meta.summary || "").trim();
  if (curated) return curated;
  const live = skill && skill.published && skill.published.description;
  return firstSentence(live || (skill && skill.description));
}

// First UTC day (YYYY-MM-DD) of a window of `days` days ending today; the
// skillUsage/{id}/dailyUses buckets are keyed the same way.
export function windowStart(days, now = Date.now()) {
  return new Date(now - (days - 1) * 864e5).toISOString().slice(0, 10);
}

// Mirrors the server's requireApprover: Max, the skill's owner, or anyone on
// the global approvers list.
export function canApproveSkill(skill, { uid, isMax, approvers = [] } = {}) {
  if (!uid) return false;
  return !!(isMax || (skill && skill.owner === uid) || approvers.includes(uid));
}

export function isLive(skill) {
  return !!(skill && skill.status === "published" && skill.published);
}

// The one thing a card says besides "live": what is pending, or why it is not
// live. null when the skill is live and nothing newer is in flight.
export function skillCardState(skill, { canApprove = false } = {}) {
  if (!skill) return null;
  const v = skill.version || 1;
  const live = isLive(skill);
  const pending = !live || v !== skill.published.version;
  if (pending && skill.reviewState === "in_review") {
    return canApprove
      ? { kind: "review", label: `v${v} waiting for you` }
      : { kind: "in_review", label: `v${v} in review` };
  }
  if (pending && skill.reviewState === "changes_requested") {
    return { kind: "changes_requested", label: `v${v} changes requested` };
  }
  if (!skill.published) return { kind: "draft", label: "Draft" };
  if (!live) return { kind: "offline", label: "Offline" };
  return null;
}

// The version number a card shows: the live one when there is one.
export function shownVersion(skill) {
  return (
    (skill && skill.published && skill.published.version) || skill.version || 1
  );
}

function matches(q, entry) {
  if (!q) return true;
  const hay = [entry.title, entry.summary, entry.category, entry.name]
    .join(" ")
    .toLocaleLowerCase();
  return q
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => hay.includes(word));
}

const isMine = (entry, uid) =>
  !!uid && (entry.owner === uid || entry.createdBy === uid);

// A version in review that this user may approve (skillCardState "review").
const isWaiting = (entry) => !!(entry.state && entry.state.kind === "review");

// Open feedback (skills/{id}.feedbackOpen) on a skill this user may approve.
const hasFeedbackForYou = (entry) =>
  !!(entry.canApprove && entry.feedbackOpen > 0);

// Category chips: every category with its count, biggest first, plus the
// "All", "Mine", "Waiting for you" and "Feedback for you" totals.
export function categoryChips(entries, { uid } = {}) {
  const counts = new Map();
  for (const e of entries) {
    const label = cleanCategory(e.category) || UNCATEGORIZED;
    const key = label.toLocaleLowerCase();
    const prev = counts.get(key);
    counts.set(key, {
      label: prev ? prev.label : label,
      count: (prev ? prev.count : 0) + 1,
    });
  }
  return {
    all: entries.length,
    mine: entries.filter((e) => isMine(e, uid)).length,
    waiting: entries.filter(isWaiting).length,
    feedback: entries.filter(hasFeedbackForYou).length,
    categories: [...counts.entries()]
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
  };
}

// Entries ({ id, title, name, summary, category, owner, createdBy, state,
// canApprove, feedbackOpen, uses30 }) filtered and grouped into one section
// per category: biggest category first; within it, skills waiting for you
// first, then those with feedback for you, then most used.
export function groupSkills(
  entries,
  {
    query = "",
    category = "",
    mine = false,
    waiting = false,
    feedback = false,
    uid,
  } = {}
) {
  const sections = new Map();
  for (const e of entries) {
    const label = cleanCategory(e.category) || UNCATEGORIZED;
    const key = label.toLocaleLowerCase();
    if (category && key !== category) continue;
    if (mine && !isMine(e, uid)) continue;
    if (waiting && !isWaiting(e)) continue;
    if (feedback && !hasFeedbackForYou(e)) continue;
    if (!matches(query.trim(), e)) continue;
    if (!sections.has(key)) {
      sections.set(key, { key, label, ramp: categoryRamp(label), skills: [] });
    }
    sections.get(key).skills.push(e);
  }
  const byUse = (a, b) =>
    isWaiting(b) - isWaiting(a) ||
    hasFeedbackForYou(b) - hasFeedbackForYou(a) ||
    (b.uses30 || 0) - (a.uses30 || 0) ||
    (a.title || a.name || "").localeCompare(b.title || b.name || "");
  return [...sections.values()]
    .map((s) => ({ ...s, skills: s.skills.sort(byUse) }))
    .sort(
      (a, b) =>
        b.skills.length - a.skills.length || a.label.localeCompare(b.label)
    );
}
